/**
 * What a piece of equipment looks like in the 3D room view (z8nrz7fw25), and
 * where it stands in its room. Pure, so the guess from a type's name and the
 * way units are set out in a room are tested rules.
 */

export const UNIT_SHAPES = ['rack', 'freezer', 'tank', 'fridge', 'cabinet', 'bench', 'box'] as const
export type UnitShape = (typeof UNIT_SHAPES)[number]

export const UNIT_SHAPE_LABELS: Record<UnitShape, string> = {
  rack: 'Rack / shelving',
  freezer: 'Upright freezer',
  tank: 'LN2 tank / dewar',
  fridge: 'Fridge',
  cabinet: 'Cabinet / incubator',
  bench: 'Bench / instrument',
  box: 'Box',
}

/**
 * A footprint (cells across and deep) and a height (in cells, drawn at the
 * view's scale): what each shape takes up in its room.
 */
export const SHAPE_SIZE: Record<UnitShape, { w: number; d: number; h: number }> = {
  rack: { w: 1.5, d: 0.6, h: 2.4 },
  freezer: { w: 1, d: 1, h: 2.4 },
  tank: { w: 1, d: 1, h: 1.6 },
  fridge: { w: 1, d: 1, h: 2.6 },
  cabinet: { w: 1.5, d: 0.8, h: 2.2 },
  bench: { w: 2, d: 0.8, h: 1 },
  box: { w: 0.6, d: 0.6, h: 0.7 },
}

/**
 * The shape a unit is drawn as when nobody chose one, from its type's name:
 * "LN2 Dewar" is a tank, "Ambient Shelving" a rack. First match wins, most
 * specific first; anything unrecognised is a box.
 */
export function guessShape(typeName: string, purpose?: string): UnitShape {
  const t = typeName.toLowerCase()
  if (/dewar|ln2|nitrogen|cryo.?tank|vapou?r|tank/.test(t)) return 'tank'
  if (/fridge|refrig|cooler|4\s?°?c/.test(t)) return 'fridge'
  if (/freezer|ult\b|ulf|-80|minus/.test(t)) return 'freezer'
  if (/rack|shelv|shelf/.test(t)) return 'rack'
  if (/incubator|cabinet|bsc|hood|isolator|locker/.test(t)) return 'cabinet'
  if (/centrifuge|bench|instrument|analy[sz]er|counter|microscope|sealer|thaw/.test(t)) return 'bench'
  if (purpose === 'STORAGE') return 'freezer'
  return 'box'
}

/** The shape to draw: the one chosen for the unit, else the guess. */
export function shapeOf(unit: { shape?: string | null; typeName: string; purpose?: string }): UnitShape {
  return (UNIT_SHAPES as readonly string[]).includes(unit.shape ?? '') ? (unit.shape as UnitShape) : guessShape(unit.typeName, unit.purpose)
}

export interface SpotInput {
  id: string
  shape: UnitShape
  /** Where someone dragged it, in cells from the room's back-left corner. */
  spot?: { x: number; y: number } | null
}

export interface UnitSpot {
  id: string
  x: number
  y: number
  w: number
  d: number
}

/**
 * Where each unit stands in a room `w` × `d` cells. A unit someone placed
 * keeps its spot (pulled back inside if the room got smaller); the others
 * line up along the back wall, half a cell apart, wrapping to the next row.
 */
export function layoutUnits(
  units: SpotInput[],
  w: number,
  d: number,
  /** For an L, U or T room: whether a footprint stands on its floor. Omitted: the whole rectangle is floor. */
  fits?: (box: { x: number; y: number; w: number; d: number }) => boolean,
): UnitSpot[] {
  if (fits) return layoutInShape(units, w, d, fits)
  const gap = 0.5
  let x = gap
  let y = gap
  let rowDepth = 0
  return units.map((u) => {
    const size = SHAPE_SIZE[u.shape]
    if (u.spot) {
      return {
        id: u.id,
        x: Math.min(Math.max(0, u.spot.x), Math.max(0, w - size.w)),
        y: Math.min(Math.max(0, u.spot.y), Math.max(0, d - size.d)),
        w: size.w,
        d: size.d,
      }
    }
    if (x > gap && x + size.w > w - gap) {
      x = gap
      y += rowDepth + gap
      rowDepth = 0
    }
    // A crowded room never spills units through its walls: the last rows
    // stand closer together instead.
    const at = {
      id: u.id,
      x: Math.min(x, Math.max(0, w - size.w)),
      y: Math.min(y, Math.max(0, d - size.d)),
      w: size.w,
      d: size.d,
    }
    x += size.w + gap
    rowDepth = Math.max(rowDepth, size.d)
    return at
  })
}

/**
 * Units in a room that is not a plain rectangle: a placed unit keeps its spot
 * when that is on the floor; every other unit takes the first free spot,
 * scanning from the back-left in half cells, that is on the floor and clear
 * of the units before it. A room too full for a unit stacks it in the corner
 * rather than drawing it through a wall.
 */
function layoutInShape(units: SpotInput[], w: number, d: number, fits: (box: { x: number; y: number; w: number; d: number }) => boolean): UnitSpot[] {
  const taken: UnitSpot[] = []
  const clear = (b: UnitSpot) =>
    taken.every((t) => b.x + b.w + 0.25 <= t.x || t.x + t.w + 0.25 <= b.x || b.y + b.d + 0.25 <= t.y || t.y + t.d + 0.25 <= b.y)
  return units.map((u) => {
    const size = SHAPE_SIZE[u.shape]
    if (u.spot) {
      const placed = { id: u.id, x: u.spot.x, y: u.spot.y, w: size.w, d: size.d }
      if (fits(placed)) {
        taken.push(placed)
        return placed
      }
    }
    for (let y = 0.5; y + size.d <= d; y += 0.5) {
      for (let x = 0.5; x + size.w <= w; x += 0.5) {
        const at = { id: u.id, x, y, w: size.w, d: size.d }
        if (fits(at) && clear(at)) {
          taken.push(at)
          return at
        }
      }
    }
    const fallback = { id: u.id, x: 0, y: Math.max(0, d - size.d), w: size.w, d: size.d }
    taken.push(fallback)
    return fallback
  })
}

/** A dragged unit's spot snapped to half cells and kept inside the room. */
export function snapSpot(x: number, y: number, size: { w: number; d: number }, room: { w: number; d: number }): { x: number; y: number } {
  const snap = (v: number) => Math.round(v * 2) / 2
  return {
    x: Math.min(Math.max(0, snap(x)), Math.max(0, room.w - size.w)),
    y: Math.min(Math.max(0, snap(y)), Math.max(0, room.d - size.d)),
  }
}
